import { useEffect, useState } from 'react';
import { api, User } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Skeleton } from '../components/Layout';
import { pageNumbers } from '../lib/paginate';

const USERS_PAGE_SIZE = 10;

export default function AdminPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [health, setHealth] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [userError, setUserError] = useState('');
  const [userBusy, setUserBusy] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [tagError, setTagError] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const [loginDomains, setLoginDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [domainBusy, setDomainBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.listUsers(),
      api.adminHealth(),
      // Resilient (a tags failure shouldn't block the rest) but the failure is
      // surfaced via tagError rather than silently shown as an empty list.
      api.adminListTags().catch(() => { setTagError(true); return { tags: [] as string[] }; }),
      api.adminListLoginDomains().catch(() => {
        setDomainError('Could not load login domains.');
        return { domains: [] as string[] };
      }),
    ]).then(([u, h, t, d]) => {
      setUsers(u.users ?? []);
      setHealth(h.status);
      setTags(t.tags ?? []);
      setLoginDomains(d.domains ?? []);
    }).finally(() => setLoading(false));
  }, []);

  // Serialize domain mutations, same pattern as tags.
  const addDomain = async () => {
    const value = newDomain.trim();
    if (!value || domainBusy) return;
    setDomainBusy(true);
    setDomainError('');
    try {
      const { domains } = await api.adminAddLoginDomain(value);
      setLoginDomains(domains);
      setNewDomain('');
    } catch (e: unknown) {
      setDomainError((e as Error).message);
    } finally {
      setDomainBusy(false);
    }
  };

  const removeDomain = async (domain: string) => {
    if (domainBusy) return;
    setDomainBusy(true);
    setDomainError('');
    try {
      const { domains } = await api.adminRemoveLoginDomain(domain);
      setLoginDomains(domains);
    } catch (e: unknown) {
      setDomainError((e as Error).message);
    } finally {
      setDomainBusy(false);
    }
  };

  // Serialize tag mutations so out-of-order responses can't clobber state.
  const addTag = async () => {
    const value = newTag.trim();
    if (!value || tagBusy) return;
    setTagBusy(true);
    setTagError(false);
    try {
      const { tags: updated } = await api.adminAddTag(value);
      setTags(updated);
      setNewTag('');
    } catch {
      setTagError(true);
    } finally {
      setTagBusy(false);
    }
  };

  const removeTag = async (tag: string) => {
    if (tagBusy) return;
    setTagBusy(true);
    setTagError(false);
    try {
      const { tags: updated } = await api.adminRemoveTag(tag);
      setTags(updated);
    } catch {
      setTagError(true);
    } finally {
      setTagBusy(false);
    }
  };

  const addUser = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || userBusy) return;
    setUserError('');
    if (users.some((u) => u.email.toLowerCase() === email)) {
      setUserError('A user with this email already exists');
      return;
    }
    setUserBusy(true);
    try {
      await api.createUser({ email });
      setNewEmail('');
      const { users: u } = await api.listUsers();
      setUsers(u ?? []);
    } catch (e: unknown) {
      setUserError((e as Error).message);
    } finally {
      setUserBusy(false);
    }
  };

  return (
    <div>
      <h1>Admin Panel</h1>
      <p className="muted">System health: {health}</p>

      <section className="card">
        <h3>Login domains</h3>
        <p className="muted">
          Anyone with an email on these domains can sign in and is added automatically.
          People on other domains must be added to the allowlist below.
        </p>
        <div className="row">
          <input
            placeholder="lionstudios.cc"
            value={newDomain}
            disabled={domainBusy}
            onChange={(e) => { setNewDomain(e.target.value); setDomainError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
          />
          <button type="button" className="btn primary" onClick={addDomain} disabled={domainBusy}>
            Add domain
          </button>
        </div>
        {domainError && <p className="error">{domainError}</p>}
        {loading
          ? <Skeleton width="100%" height="2rem" radius="6px" style={{ marginTop: '1rem' }} />
          : (
            <div className="tag-chips" style={{ marginTop: '1rem' }}>
              {loginDomains.map((domain) => (
                <span key={domain} className="chip">
                  @{domain}
                  {loginDomains.length > 1 && (
                    <button
                      type="button"
                      className="chip-remove"
                      aria-label={`Remove ${domain}`}
                      onClick={() => removeDomain(domain)}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
      </section>

      <section className="card">
        <h3>Allowlist</h3>
        <p className="muted">
          Emails outside the login domains are added as external accounts
          (no access until you upgrade them to a user).
        </p>
        <div className="row">
          <input
            type="email"
            placeholder="user@lionstudios.cc"
            value={newEmail}
            onChange={(e) => { setNewEmail(e.target.value); setUserError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') addUser(); }}
          />
          <button type="button" className="btn primary" onClick={addUser} disabled={userBusy}>Add user</button>
        </div>
        {userError && <p className="error">{userError}</p>}
        <input
          type="search"
          className="users-search"
          placeholder="Search users…"
          aria-label="Search users"
          value={userSearch}
          onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }}
        />
        {loading
          ? <>{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} width="100%" height="2.2rem" radius="4px" style={{ marginBottom: '2px' }} />)}</>
          : (() => {
              const query = userSearch.trim().toLowerCase();
              const filtered = query
                ? users.filter((u) =>
                    u.email.toLowerCase().includes(query)
                    || (u.displayName ?? '').toLowerCase().includes(query))
                : users;
              const totalPages = Math.max(1, Math.ceil(filtered.length / USERS_PAGE_SIZE));
              const page = Math.min(userPage, totalPages);
              const paged = filtered.slice((page - 1) * USERS_PAGE_SIZE, page * USERS_PAGE_SIZE);
              return (
                <>
                  <table>
                    <thead>
                      <tr><th>Email</th><th>Role</th><th></th></tr>
                    </thead>
                    <tbody>
                      {paged.map((u) => (
                        <tr key={u.userId}>
                          <td>{u.email}</td>
                          <td>{u.role}</td>
                                                    <td className="users-actions">
                            {u.role === 'external' && (
                              <button
                                type="button"
                                className="btn ghost"
                                title="Give this external account full access as a normal user"
                                onClick={() => api.updateUser(u.userId, { role: 'user' })
                                  .then(() => api.listUsers().then((r) => setUsers(r.users ?? [])))
                                  .catch((e: Error) => setUserError(e.message))}
                              >
                                Upgrade to user
                              </button>
                            )}
                            {u.userId !== me?.userId && (
                              <button
                                type="button"
                                className="btn ghost users-delete"
                                onClick={() => {
                                  if (!window.confirm(`Delete ${u.email}? They lose access immediately.`)) return;
                                  api.deleteUser(u.userId)
                                    .then(() => api.listUsers().then((r) => setUsers(r.users ?? [])))
                                    .catch((e: Error) => setUserError(e.message));
                                }}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!paged.length && (
                        <tr>
                          <td colSpan={3} className="muted">
                            {userSearch ? `No users match "${userSearch}".` : 'No users yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <nav className="pagination" aria-label="Users pagination">
                      <button type="button" className="btn ghost" onClick={() => setUserPage(page - 1)} disabled={page === 1}>
                        Prev
                      </button>
                      {pageNumbers(page, totalPages).map((p, i) =>
                        p === '…' ? (
                          <span key={`gap-${i}`} className="pagination-gap muted">…</span>
                        ) : (
                          <button
                            key={p}
                            type="button"
                            className={`btn ghost pagination-page${p === page ? ' active' : ''}`}
                            onClick={() => setUserPage(p)}
                            aria-current={p === page ? 'page' : undefined}
                          >
                            {p}
                          </button>
                        ),
                      )}
                      <button type="button" className="btn ghost" onClick={() => setUserPage(page + 1)} disabled={page === totalPages}>
                        Next
                      </button>
                    </nav>
                  )}
                </>
              );
            })()}
      </section>

      <section className="card">
        <h3>Tags</h3>
        <p className="muted">Manage the tag list users can choose from when uploading or browsing.</p>
        <div className="row">
          <input
            placeholder="new-tag"
            value={newTag}
            disabled={tagBusy}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          />
          <button type="button" className="btn primary" onClick={addTag} disabled={tagBusy}>Add tag</button>
        </div>
        {loading
          ? <Skeleton width="100%" height="2rem" radius="6px" style={{ marginTop: '1rem' }} />
          : tagError
            ? <p className="error" style={{ marginTop: '1rem' }}>Couldn’t load tags. Try refreshing.</p>
            : tags.length
            ? (
              <div className="tag-chips" style={{ marginTop: '1rem' }}>
                {tags.map((t) => (
                  <span key={t} className="chip">
                    {t}
                    <button
                      type="button"
                      className="chip-remove"
                      aria-label={`Remove ${t}`}
                      onClick={() => removeTag(t)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )
            : <p className="muted" style={{ marginTop: '1rem' }}>No tags yet.</p>
        }
      </section>

    </div>
  );
}
